import React, {useCallback, useMemo, useState} from "react";
import {DialogContent, DialogTitle, Divider, MenuItem, Typography} from "@mui/material";
import Dialog from "../../base/dialog";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from "../../form";
import Button from "../../base/button/button";
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";
import useAccessLevels from "../../../hooks/use-access-levels";

const ImportProductsWithAIDialog = ({open, close, onImport, warehouses}) => {
    const [importing, setImporting] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    const schema = useMemo(() => Yup.object().shape({
        value: Yup.string().nullable().required(ValidateMessages.required).trim(),
        replace: Yup.boolean().nullable().required(ValidateMessages.required).default(false),
        ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
            warehouse: Yup.mixed().nullable().required(ValidateMessages.required),
        }),
    }), [operatorAccessLevels.packages.sales.manageWarehousesInQuote]);

    const initialValues = useMemo(() => ({
        value: '',
        replace: false,
        ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
            warehouse: warehouses?.find(e => e.code === '00') ?? warehouses?.[0] ?? null,
        }),
    }), [operatorAccessLevels.packages.sales.manageWarehousesInQuote, warehouses])

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Callback function passed to the AI import section
     * @type {(function(*[],*=): void)}
     */
    const onImportWithAISuccess = useCallback((data, warehouse, replace) => {
        let items = data
            .map(e => {
                const quantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? (e.qty ?? 0) : Math.trunc(e.qty ?? 0);
                return ({
                    partNo: e.partNumber?.toUpperCase()?.trim(),
                    quantity: quantity,
                    warehouseCode: warehouse?.code,
                    price: e.price,
                })
            }).filter(e => !!e.partNo)
        if (!operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders) {
            // need to merge the items with the same part number
            let mergedItems = [];
            items.forEach(item => {
                let found = mergedItems.find(e => e.partNo === item.partNo);
                if (found) {
                    found.quantity += item.quantity;
                } else {
                    mergedItems.push(item);
                }
            });
            items = mergedItems;
        }
        onImport(items, replace);
    }, [onImport, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders, operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders])

    /**
     * Callback function to import the items with AI
     * @type {(function(): Promise<void>)}
     */
    const importItemsWithAI = useCallback(async (values) => {
        setImporting(true);
        const response = await Api.importPartsWithAI({
            text: values.value,
        });
        if (!isMounted())
            return;
        setImporting(false);
        if (response?.resultFlag) {
            onImportWithAISuccess(response.data?.items ?? [], values.warehouse, values.replace);
            close();
        }
    }, [close, isMounted, onImportWithAISuccess])

    return (
        <Dialog
            open={open ?? false}
            setOpen={close}
            maxWidth={'sm'}
            className={'bound-dialog quick-order-dialog'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    Import Products with AI
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent className={'d-flex flex-column p-3'}>
                <Typography variant={'body2'} className={'mb-4'}>
                    You can import your desired parts by copying any text containing part numbers and quantities.
                    Be sure to also select your desired warehouse.
                </Typography>
                <Form
                    onSubmit={importItemsWithAI}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{className: 'd-flex flex-column'}}
                >
                    {
                        ({values}) => (
                            <>
                                {
                                    operatorAccessLevels.packages.sales.manageWarehousesInQuote &&
                                    <MuiRFF.TextField
                                        select
                                        fullWidth
                                        variant={'outlined'}
                                        label={'Warehouse'}
                                        name={'warehouse'}
                                        required={required.warehouse}
                                        disabled={importing}
                                        className={'mb-3'}
                                        SelectProps={{
                                            renderValue: (value) => value ? `${value.name} (${value.code})` : '',
                                        }}
                                    >
                                        {
                                            warehouses?.map(warehouse => (
                                                <MenuItem key={warehouse.id} value={warehouse}>
                                                    {warehouse.name} ({warehouse.code})
                                                </MenuItem>
                                            )) ?? []
                                        }
                                    </MuiRFF.TextField>
                                }
                                <MuiRFF.TextField
                                    fullWidth
                                    multiline
                                    minRows={10}
                                    maxRows={20}
                                    variant={'outlined'}
                                    label={'Content to be imported'}
                                    placeholder={'Paste the content here'}
                                    name={'value'}
                                    required={required.value}
                                    disabled={importing}
                                    className={'mb-3'}
                                />
                                <MuiRFF.Checkboxes
                                    color={'primary'}
                                    icon={<CheckboxIcon/>}
                                    checkedIcon={<ActiveCheckboxIcon/>}
                                    data={{
                                        value: values.replace,
                                        label: 'Use the imported quantity to replace the existing quantity and reset affected items prices.',
                                        disabled: importing
                                    }}
                                    name={'replace'}
                                    disabled={importing}
                                    required={required.replace}
                                    sx={{marginBlockEnd: 0, marginInlineEnd: 0,}}
                                />
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    size={'large'}
                                    type={'submit'}
                                    className={'mt-3'}
                                    disabled={importing}
                                >
                                    {importing ? "Importing your content" : "Import"}
                                </Button>
                            </>
                        )
                    }
                </Form>
            </DialogContent>
        </Dialog>
    );
}

export default ImportProductsWithAIDialog;